import { z } from "zod";
import type { CatalogGetCoursesParams } from "@/types/api";

/** Rows per page of the All courses table. */
export const ALL_COURSES_PAGE_SIZE = 8;

export const COURSE_SORTS = [
  "title-asc",
  "title-desc",
  "level-asc",
  "level-desc",
  "length-asc",
  "length-desc",
  "price-asc",
  "price-desc",
  "newest",
  "popular",
] as const;

export type CourseSort = (typeof COURSE_SORTS)[number];

/** The table's default order, Course A→Z. It is left out of the URL. */
export const DEFAULT_COURSE_SORT: CourseSort = "title-asc";

/** The "Most popular" and "Newest" toggles: each replaces the column sort while it is on. */
export type QuickSort = Extract<CourseSort, "popular" | "newest">;

/** The sortable table columns. "Who it's for" isn't sortable: a set of audiences has no meaningful order. */
export type SortColumn = "title" | "level" | "length" | "price";

/** An option of a filter in the Filters popover. */
export interface CourseFilterOption {
  id: string;
  label: string;
  /** Not offered, and not applied, while the learning-paths flag is off: the table then lists no paths. */
  requiresLearningPaths?: boolean;
  /** Not offered, and not applied, while the ilt flag is off: the table then lists no face-to-face courses. */
  requiresIlt?: boolean;
}

/** A filter in the Filters popover. Add an entry here (and to the URL schema) to add a filter. */
export interface CourseFilterDefinition<K extends string = string> {
  /** The URL search param, also sent to `GET /api/catalog/courses`. */
  key: K;
  label: string;
  /** The option that clears the filter. */
  anyLabel: string;
  options: readonly CourseFilterOption[];
  /** Hidden, and not applied, while the payments flag is off: every visible course is then free. */
  requiresPayments?: boolean;
}

export type CourseFilterKey = "type" | "level" | "audience" | "price";

/** The `type` of a learning path row (`CourseListItemDto.type`) and of the type filter's "Learning paths" option. */
export const LEARNING_PATH_TYPE = "learning-path";

/**
 * The `type` of an instructor-led course row (`CourseListItemDto.type`, LMS-183) and of the type filter's
 * "Face-to-face courses" option.
 */
export const ILT_COURSE_TYPE = "ilt-course";

export const COURSE_FILTERS: readonly CourseFilterDefinition<CourseFilterKey>[] = [
  {
    // Add an option here (and on the API's CatalogItemTypes) for a new kind of course.
    key: "type",
    label: "Type",
    anyLabel: "All",
    options: [
      { id: "course", label: "Online courses" },
      { id: ILT_COURSE_TYPE, label: "Face-to-face courses", requiresIlt: true },
      { id: LEARNING_PATH_TYPE, label: "Learning paths", requiresLearningPaths: true },
    ],
  },
  {
    key: "level",
    label: "Level",
    anyLabel: "All levels",
    options: [
      { id: "Introduction", label: "Introduction" },
      { id: "Intermediate", label: "Intermediate" },
      { id: "Advanced", label: "Advanced" },
      { id: "Expert", label: "Expert" },
    ],
  },
  {
    key: "audience",
    label: "Who it's for",
    anyLabel: "Everyone",
    options: [
      { id: "HealthcareWorkers", label: "Healthcare workers" },
      { id: "SocialWorkers", label: "Social workers" },
      { id: "SocialCareWorkers", label: "Social care workers" },
      { id: "EducationWorkers", label: "Education workers" },
      { id: "ParentsAndCarers", label: "Parents and carers" },
      { id: "Other", label: "Other" },
    ],
  },
  {
    key: "price",
    label: "Price",
    anyLabel: "Any price",
    options: [
      { id: "free", label: "Free" },
      { id: "paid", label: "Paid" },
    ],
    requiresPayments: true,
  },
];

const filterValues = (key: CourseFilterKey) =>
  COURSE_FILTERS.find((f) => f.key === key)!.options.map((option) => option.id) as [
    string,
    ...string[],
  ];

// Search params arrive JSON-parsed (`?q=123` is the number 123). Anything invalid is dropped rather than failing the
// route, so an old or hand-edited link still opens the page.
const optionalEnum = (values: [string, ...string[]]) => z.enum(values).optional().catch(undefined);

export const allCoursesSearchSchema = z.object({
  q: z
    .preprocess(
      (value) => (typeof value === "number" ? String(value) : value),
      z.string().trim().min(1).optional(),
    )
    .catch(undefined),
  type: optionalEnum(filterValues("type")),
  level: optionalEnum(filterValues("level")),
  audience: optionalEnum(filterValues("audience")),
  price: optionalEnum(filterValues("price")),
  sort: z.enum(COURSE_SORTS).optional().catch(undefined),
  page: z.coerce.number().int().min(2).optional().catch(undefined),
});

export type AllCoursesSearch = z.infer<typeof allCoursesSearchSchema>;

export interface AllCoursesFlags {
  paymentsEnabled: boolean;
  /** Off (no paths, no Learning paths type) when left out, as the flag reads until it loads. */
  learningPathsEnabled?: boolean;
  /** Off (no face-to-face courses, no Face-to-face type) when left out, as the flag reads until it loads. */
  iltEnabled?: boolean;
}

export interface AllCoursesContext extends AllCoursesFlags {
  /** Undefined until the filter options load: the popular sort is kept until then. */
  popularSortAvailable: boolean | undefined;
}

/**
 * The filters the popover offers, with the options the flags allow: no Price filter while payments are off, no
 * Learning paths type while learning paths are off and no Face-to-face type while ilt is off. A filter left with one
 * option (the Type filter's Online courses, while paths and ilt are both off) is hidden: it would filter nothing.
 */
export function getAvailableFilters({
  paymentsEnabled,
  learningPathsEnabled = false,
  iltEnabled = false,
}: AllCoursesFlags): CourseFilterDefinition<CourseFilterKey>[] {
  return COURSE_FILTERS.flatMap((filter) => {
    if (filter.requiresPayments && !paymentsEnabled) return [];
    const options = filter.options.filter(
      (option) =>
        (learningPathsEnabled || !option.requiresLearningPaths) &&
        (iltEnabled || !option.requiresIlt),
    );
    return options.length > 1 ? [{ ...filter, options }] : [];
  });
}

/**
 * The view actually shown: a filter value the flags don't offer is dropped (`getAvailableFilters`), so are the price
 * sort while payments are off and the popular sort once the API says it isn't available.
 */
export function getEffectiveSearch(
  search: AllCoursesSearch,
  { popularSortAvailable, ...flags }: AllCoursesContext,
): AllCoursesSearch & { sort: CourseSort } {
  let sort = search.sort ?? DEFAULT_COURSE_SORT;
  if (!flags.paymentsEnabled && sort.startsWith("price-")) sort = DEFAULT_COURSE_SORT;
  if (sort === "popular" && popularSortAvailable === false) sort = DEFAULT_COURSE_SORT;

  const available = getAvailableFilters(flags);
  const offered = <V extends string>(key: CourseFilterKey, value: V | undefined) =>
    available.some((filter) => filter.key === key && filter.options.some((o) => o.id === value))
      ? value
      : undefined;

  return {
    ...search,
    type: offered("type", search.type),
    price: offered("price", search.price),
    sort,
  };
}

/**
 * The `GET /api/catalog/courses` params for a view. With no type filter it asks for every type: the API lists paths
 * only while the learning-paths flag is on, and face-to-face courses only while the ilt flag is on.
 */
export function toCoursesParams(
  search: AllCoursesSearch & { sort: CourseSort },
): CatalogGetCoursesParams {
  return {
    page: search.page ?? 1,
    pageSize: ALL_COURSES_PAGE_SIZE,
    search: search.q,
    level: search.level,
    audience: search.audience,
    price: search.price,
    type: search.type ?? "all",
    sortBy: search.sort,
  };
}

/** The filters in effect, in popover order, with their option labels: drives the badge count and summary. */
export function getActiveFilters(search: AllCoursesSearch) {
  return COURSE_FILTERS.flatMap((filter) => {
    const value = search[filter.key];
    const option = filter.options.find((o) => o.id === value);
    return option ? [{ key: filter.key, label: option.label }] : [];
  });
}

export const getQuickSort = (sort: CourseSort): QuickSort | null =>
  sort === "popular" || sort === "newest" ? sort : null;

/** The column and direction of a column sort, or null while a toggle sort is on. */
export function getColumnSort(
  sort: CourseSort,
): { column: SortColumn; direction: "ascending" | "descending" } | null {
  if (getQuickSort(sort)) return null;
  const [column, direction] = sort.split("-") as [SortColumn, "asc" | "desc"];
  return { column, direction: direction === "desc" ? "descending" : "ascending" };
}

export const toColumnSort = (column: SortColumn, direction: "ascending" | "descending") =>
  `${column}-${direction === "descending" ? "desc" : "asc"}` as CourseSort;

/** Drops the default sort and first page so they stay out of the URL. */
export function normalizeSearch(search: AllCoursesSearch): AllCoursesSearch {
  return {
    q: search.q || undefined,
    type: search.type,
    level: search.level,
    audience: search.audience,
    price: search.price,
    sort: search.sort === DEFAULT_COURSE_SORT ? undefined : search.sort,
    page: search.page && search.page > 1 ? search.page : undefined,
  };
}
